import {
    useCallback,
    useEffect,
    Fragment,
    useState,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    type PointerEvent,
    Platform,
    View
} from "react-native";
import type IToastStackProps from "./type";
import type {
    ToastStackNativePropsType,
    ToastStackFocusEventType,
    ToastStackWebPropsType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    ToastItemLayoutConstantType,
    ToastItemConstantType,
    ToastSize
} from "../../type";
import {
    getIsToastFocusVisible,
    getIsToastFocusInside,
    getToastStackLayout
} from "../../util";
import {
    ToastStackContext
} from "../../context";
import toastStylesheet from "../../stylesheet";

const ToastStack: FC<IToastStackProps> = ({
    isAlwaysExpanded = false,
    anchorStyle,
    children,
    align,
    side,
    ids
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const isWeb = Platform.OS === "web";

    const [
        sizes,
        setSizes
    ] = useState<Record<string, ToastSize>>({
    });

    const [
        closingIds,
        setClosingIds
    ] = useState<Array<string>>([]);

    const [
        isTouchExpanded,
        setIsTouchExpanded
    ] = useState(false);

    const [
        isHovered,
        setIsHovered
    ] = useState(false);

    const [
        isFocused,
        setIsFocused
    ] = useState(false);

    const [
        isPressed,
        setIsPressed
    ] = useState(false);

    const anchorRef = useRef<View>(null);

    const isTouchPointer = useRef(!isWeb);

    const removePressListeners = useRef<(() => void) | undefined>(undefined);

    const activeIds = useMemo(() => ids.filter((id) => !closingIds.includes(id)), [
        closingIds,
        ids
    ]);

    const count = activeIds.length;

    const isInteractionExpanded = count > 1 && (isHovered || isFocused || isTouchExpanded || (isPressed && !isTouchPointer.current));

    const isExpanded = count > 1 && (isAlwaysExpanded || isInteractionExpanded);

    const isTapExpandable = count > 1 && !isExpanded && isTouchPointer.current;

    const isPaused = isInteractionExpanded || isPressed;

    const layout = useMemo(() => getToastStackLayout({
        layers: shapeConfigs.layers,
        gap: spaces.spacingMd,
        ids: activeIds,
        isExpanded,
        direction,
        align,
        sizes,
        side
    }), [
        shapeConfigs.layers,
        spaces.spacingMd,
        isExpanded,
        activeIds,
        direction,
        align,
        sizes,
        side
    ]);

    useEffect(() => {
        if(count < 2) {
            setIsTouchExpanded(false);
        }
    }, [
        count
    ]);

    useEffect(() => {
        return () => {
            removePressListeners.current?.();
        };
    }, []);

    const handleItemLayout = useCallback(({
        height,
        width,
        id
    }: ToastItemLayoutConstantType) => {
        setSizes((current) => {
            const size = current[id];

            if(size && size.height === height && size.width === width) {
                return current;
            }

            return {
                ...current,
                [id]: {
                    height,
                    width
                }
            };
        });
    }, []);

    const handleItemClosing = useCallback(({
        id
    }: ToastItemConstantType) => {
        setClosingIds((current) => current.includes(id) ? current : [
            ...current,
            id
        ]);
    }, []);

    const handleItemRemove = useCallback(({
        id
    }: ToastItemConstantType) => {
        setSizes((current) => {
            if(!(id in current)) {
                return current;
            }

            const next = {
                ...current
            };

            delete next[id];

            return next;
        });

        setClosingIds((current) => current.includes(id) ? current.filter((closingId) => closingId !== id) : current);

        if(!isWeb) {
            return;
        }

        const isFocusInside = getIsToastFocusInside({
            relatedTarget: document.activeElement,
            currentTarget: anchorRef.current
        });

        setIsFocused((current) => current && isFocusInside);
    }, []);

    const handleExpandRequest = useCallback(() => {
        setIsTouchExpanded(true);
    }, []);

    const handleOverlayPress = () => {
        setIsTouchExpanded(false);
    };

    const onPointerEnter = (event: PointerEvent) => {
        isTouchPointer.current = event.nativeEvent.pointerType === "touch";

        if(isTouchPointer.current) {
            return;
        }

        setIsHovered(true);
    };

    const onPointerLeave = (event: PointerEvent) => {
        if(event.nativeEvent.pointerType === "touch") {
            return;
        }

        setIsHovered(false);
    };

    const onPointerDown = (event: PointerEvent) => {
        isTouchPointer.current = event.nativeEvent.pointerType === "touch";

        removePressListeners.current?.();

        const handleRelease = () => {
            removePressListeners.current?.();

            setIsPressed(false);
        };

        globalThis.addEventListener("pointercancel", handleRelease);

        globalThis.addEventListener("pointerup", handleRelease);

        removePressListeners.current = () => {
            globalThis.removeEventListener("pointercancel", handleRelease);

            globalThis.removeEventListener("pointerup", handleRelease);

            removePressListeners.current = undefined;
        };

        setIsPressed(true);
    };

    const onTouchStart = () => {
        isTouchPointer.current = true;

        setIsPressed(true);
    };

    const onTouchEnd = () => {
        setIsPressed(false);
    };

    const onFocus = (event: ToastStackFocusEventType) => {
        if(getIsToastFocusVisible({
            target: event.target
        })) {
            setIsFocused(true);
        }
    };

    const onBlur = (event: ToastStackFocusEventType) => {
        if(getIsToastFocusInside({
            relatedTarget: event.relatedTarget,
            currentTarget: event.currentTarget
        })) {
            return;
        }

        setIsFocused(false);
    };

    const webProps: ToastStackWebPropsType = isWeb ? {
        onPointerDown,
        onFocus,
        onBlur
    } : {};

    const nativeProps: ToastStackNativePropsType = isWeb ? {} : {
        onTouchCancel: onTouchEnd,
        onTouchStart,
        onTouchEnd
    };

    const context = useMemo(() => ({
        onExpandRequest: handleExpandRequest,
        onItemClosing: handleItemClosing,
        onItemLayout: handleItemLayout,
        onItemRemove: handleItemRemove,
        isTapExpandable,
        isExpanded,
        isPaused,
        layout
    }), [
        handleExpandRequest,
        handleItemClosing,
        handleItemLayout,
        handleItemRemove,
        isTapExpandable,
        isExpanded,
        isPaused,
        layout
    ]);

    const renderOverlay = () => {
        if(!isTouchExpanded || count < 2) {
            return null;
        }

        return <View
            onStartShouldSetResponder={() => true}
            onResponderGrant={handleOverlayPress}
            style={stylesheet.overlay}
            aria-hidden={true}
        />;
    };

    return <Fragment>
        {renderOverlay()}
        <View
            {...nativeProps}
            {...webProps}
            onPointerLeave={onPointerLeave}
            onPointerEnter={onPointerEnter}
            style={[
                toastStylesheet.anchor,
                anchorStyle
            ]}
            ref={anchorRef}
        >
            <ToastStackContext.Provider
                value={context}
            >
                {children}
            </ToastStackContext.Provider>
        </View>
    </Fragment>;
};
export default ToastStack;
